// @flow

import * as React from 'react';


import type {CalculationResultDetails, CalculationResultDetailsBySource} from "../common/RequestTypes";
import ColorManager from "../common/ColorManager";
import type {Color} from "../common/ColorManager";
import OptionManager from "../common/OptionManager";
import ColorBadge from './ColorBadge';
import TableHeader from "./TableHeader";
import ReactAI from "react-appinsights";

type Props = {
    calculationResultDetails: CalculationResultDetails,
    techniquesToCompare: number,
    optionManager: OptionManager,
    unit: string
};


class ClimateEffectDetailsTable extends React.Component<Props> {

    colorManager: ColorManager;

    constructor() {
        super();
        this.colorManager = new ColorManager();
    }

    render() {
        
        // Every dataset corresponds to a source.
        // Only add sources where total for all techs > 0
        // Only add used techs
        
        
        
        let rows = [];

        let totalsPerSource = this.getTotalsPerSource(this.props.calculationResultDetails);

        if(totalsPerSource.tubeMaterial !== 0) {
            let sourceId = 'tubeMaterial';
            let sourceName = 'Rörmaterial';
            rows = [...rows, this.createRow(this.props.calculationResultDetails, sourceName, sourceId, this.colorManager.colorTubeMaterial)];
        }
        if(totalsPerSource.filling !== 0) {
            let sourceId = 'filling';
            let sourceName = 'Fyllnadsmassor';
            rows = [...rows, this.createRow(this.props.calculationResultDetails, sourceName, sourceId, this.colorManager.colorFilling)];
        }
        if(totalsPerSource.asphalt !== 0) {
            let sourceId = 'asphalt';
            let sourceName = 'Asfalt';
            rows = [...rows, this.createRow(this.props.calculationResultDetails, sourceName, sourceId, this.colorManager.colorAsphalt)];
        }
        if(totalsPerSource.energy !== 0) {
            let sourceId = 'energy';
            let sourceName = 'Energi';
            rows = [...rows, this.createRow(this.props.calculationResultDetails, sourceName, sourceId, this.colorManager.colorEnergy)];
        }
        if(totalsPerSource.transports !== 0) {
            let sourceId = 'transports';
            let sourceName = 'Transporter';
            rows = [...rows, this.createRow(this.props.calculationResultDetails, sourceName, sourceId, this.colorManager.colorTransports)];
        }
        if(totalsPerSource.waste !== 0) {
            let sourceId = 'waste';
            let sourceName = 'Avfall';
            rows = [...rows, this.createRow(this.props.calculationResultDetails, sourceName, sourceId, this.colorManager.colorWaste)];
        }

        let recycleRow = null;
        if(totalsPerSource.recyclePotential !== 0) {
            let sourceId = 'recyclePotential';
            let sourceName = 'Återvinningspotential';
            recycleRow = this.createRecycleRow(this.props.calculationResultDetails, sourceName, sourceId, this.colorManager.colorRecycle, 'recycle');
        }

        return (
            <table className="table table-borderless table-customstriped table-sm">
                <thead>
                <tr>
                { this.getHeaders() }
                </tr>
                </thead>
                <tbody>
                { rows }
                { this.createTotalRow(this.props.calculationResultDetails) }
                { recycleRow }
                </tbody>
            </table>
        );
    }

    getHeaders() {
        let headers = [];
        
        let unitHtml = {__html: this.props.unit};
        
        headers.push(<th key="0"></th>); // Color badge header
        headers.push(<th key="1"></th>); // Category header
        
        if(this.props.optionManager.isInsertionFlexibleLiningUsed(this.props.techniquesToCompare)) {
            headers.push(<th key="2">Infodring flexibla foder (<span dangerouslySetInnerHTML={unitHtml} />)</th>);
        }
        if(this.props.optionManager.isTubeCrackingUsed(this.props.techniquesToCompare)) {
            headers.push(<th key="3">Rörspräckning (<span dangerouslySetInnerHTML={unitHtml} />)</th>);
        }
        if(this.props.optionManager.isDirectedDrillingUsed(this.props.techniquesToCompare)) {
            headers.push(<th key="4">Styrd borrning (<span dangerouslySetInnerHTML={unitHtml} />)</th>);
        }
        if(this.props.optionManager.isSlipliningUsed(this.props.techniquesToCompare)) {
            headers.push(<th key="5">Sliplining (<span dangerouslySetInnerHTML={unitHtml} />)</th>);
        }
        if(this.props.optionManager.isPuLiningUsed(this.props.techniquesToCompare)) {
            headers.push(<th key="6">PU liner (<span dangerouslySetInnerHTML={unitHtml} />)</th>);
        }

        headers.push(<th key="100">Traditionell grävning (<span dangerouslySetInnerHTML={unitHtml} />)</th>);

        return headers;
    }
    
    createRow(resultDetails: CalculationResultDetails, sourceName: string, sourceId: string, color: Color, rowClass: ?string) {
        
        let colorString = this.colorManager.getRgbaString(color);

        return (<tr key={sourceId} className={rowClass}>
            <td><ColorBadge colorString={colorString} /></td>
            <th scope="row">{sourceName}</th>
            { this.getCellsForSource(resultDetails, sourceId) }
        </tr>);
    }

    createRecycleRow(resultDetails: CalculationResultDetails, sourceName: string, sourceId: string, color: Color, rowClass: ?string) {
        
        let colorString = this.colorManager.getRgbaString(color);
        const htmlHelp = `<p>Återvinningspotentialen är den "miljövinst" som är möjlig att tillgodoräkna sig i nästa livscykel/framtida projekt genom att använda återvunnet material från detta projekt.</p>`;

        return (<tr key={sourceId} className={rowClass}>
            <td><ColorBadge colorString={colorString} /></td>
            <th scope="row"><TableHeader text={sourceName} helpHtml={htmlHelp}/></th>
            { this.getCellsForSource(resultDetails, sourceId) }
        </tr>);
    }

    createTotalRow(resultDetails: CalculationResultDetails) {
        
        return (<tr>
            <td></td>
            <th scope="row"><em>Totalt</em></th>
            { this.getTotalCells(resultDetails) }
        </tr>);
    }

    getCellsForSource(resultDetails: CalculationResultDetails, sourceId: string) {
        let retVal = [];
        if(this.props.optionManager.isInsertionFlexibleLiningUsed(this.props.techniquesToCompare)) {
            retVal.push(<td key="1">{this.round(resultDetails.insertionFlexibleLining[sourceId], 0)}</td>);
        }
        if(this.props.optionManager.isTubeCrackingUsed(this.props.techniquesToCompare)) {
            retVal.push(<td key="2">{this.round(resultDetails.tubeCracking[sourceId], 0)}</td>);
        }
        if(this.props.optionManager.isDirectedDrillingUsed(this.props.techniquesToCompare)) {
            retVal.push(<td key="3">{this.round(resultDetails.directedDrilling[sourceId], 0)}</td>);
        }
        if(this.props.optionManager.isSlipliningUsed(this.props.techniquesToCompare)) {
            retVal.push(<td key="4">{this.round(resultDetails.slipLining[sourceId], 0)}</td>);
        }
        if(this.props.optionManager.isPuLiningUsed(this.props.techniquesToCompare)) {
            retVal.push(<td key="5">{this.round(resultDetails.puLiner[sourceId], 0)}</td>);
        }

        retVal.push(<td key="100">{this.round(resultDetails.traditionalDigging[sourceId], 0)}</td>);

        return retVal;
    }

    getTotalCells(resultDetails: CalculationResultDetails) {
        let retVal = [];
        if(this.props.optionManager.isInsertionFlexibleLiningUsed(this.props.techniquesToCompare)) {
            retVal.push(<td key="1"><strong><em>{this.round(resultDetails.insertionFlexibleLining.totalDerived, 0)}</em></strong></td>);
        }
        if(this.props.optionManager.isTubeCrackingUsed(this.props.techniquesToCompare)) {
            retVal.push(<td key="2"><strong><em>{this.round(resultDetails.tubeCracking.totalDerived, 0)}</em></strong></td>);
        }
        if(this.props.optionManager.isDirectedDrillingUsed(this.props.techniquesToCompare)) {
            retVal.push(<td key="3"><strong><em>{this.round(resultDetails.directedDrilling.totalDerived, 0)}</em></strong></td>);
        }
        if(this.props.optionManager.isSlipliningUsed(this.props.techniquesToCompare)) {
            retVal.push(<td key="4"><strong><em>{this.round(resultDetails.slipLining.totalDerived, 0)}</em></strong></td>);
        }
        if(this.props.optionManager.isPuLiningUsed(this.props.techniquesToCompare)) {
            retVal.push(<td key="5"><strong><em>{this.round(resultDetails.puLiner.totalDerived, 0)}</em></strong></td>);
        }

        retVal.push(<td key="100"><strong><em>{this.round(resultDetails.traditionalDigging.totalDerived, 0)}</em></strong></td>);

        return retVal;
    }


    getTotalsPerSource(resultDetails: CalculationResultDetails): CalculationResultDetailsBySource {
        return {
            tubeMaterial: this.getTotalBySource(resultDetails, 'tubeMaterial'),
            filling: this.getTotalBySource(resultDetails, 'filling'),
            asphalt: this.getTotalBySource(resultDetails, 'asphalt'),
            energy: this.getTotalBySource(resultDetails, 'energy'),
            transports: this.getTotalBySource(resultDetails, 'transports'),
            waste: this.getTotalBySource(resultDetails, 'waste'),
            // total: this.getTotalBySource(resultDetails, 'total'),
            totalDerived: this.getTotalBySource(resultDetails, 'totalDerived'),
            recyclePotential: this.getTotalBySource(resultDetails, 'recyclePotential'),
        }
    }

    getTotalBySource(resultDetails: CalculationResultDetails, sourceName: string): number {
        let totalBySource = 0;
        for(let techDetails of Object.values(resultDetails)) {
            // $FlowFixMe 
            if(typeof techDetails[sourceName] === 'number')
                totalBySource += techDetails[sourceName];
        }
        return totalBySource;
    }

    round(value: number, numDecimals: number): string {
        if(value === 0) {
            return "-";
        }
        else {
            let rounded = parseFloat(value.toFixed(numDecimals));
            if(this.detectIE() === false)
                return rounded.toLocaleString("se-SV"); // Not OK in IE 10 and 11
            else
                return rounded.toString().replace(".", ",");
        }
    }

    detectIE() {
        let ua = window.navigator.userAgent;

        // Test values; Uncomment to check result …

        // IE 10
        // ua = 'Mozilla/5.0 (compatible; MSIE 10.0; Windows NT 6.2; Trident/6.0)';

        // IE 11
        // ua = 'Mozilla/5.0 (Windows NT 6.3; Trident/7.0; rv:11.0) like Gecko';

        // Edge 12 (Spartan)
        // ua = 'Mozilla/5.0 (Windows NT 10.0; WOW64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/39.0.2171.71 Safari/537.36 Edge/12.0';

        // Edge 13
        // ua = 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/46.0.2486.0 Safari/537.36 Edge/13.10586';

        let msie = ua.indexOf('MSIE ');
        if (msie > 0) {
            // IE 10 or older => return version number
            return parseInt(ua.substring(msie + 5, ua.indexOf('.', msie)), 10);
        }

        let trident = ua.indexOf('Trident/');
        if (trident > 0) {
            // IE 11 => return version number
            let rv = ua.indexOf('rv:');
            return parseInt(ua.substring(rv + 3, ua.indexOf('.', rv)), 10);
        }

        let edge = ua.indexOf('Edge/');
        if (edge > 0) {
            // Edge (IE 12+) => return version number
            return parseInt(ua.substring(edge + 5, ua.indexOf('.', edge)), 10);
        }

        // other browser
        return false;
    }

}

export default ReactAI.withTracking(ClimateEffectDetailsTable);





// WEBPACK FOOTER //
// ./src/components/ClimateEffectDetailsTable.js